import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import { fetchSpecialist, specialistKey } from '../api/specialists'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { ClinicSessions } from '../components/specialists/ClinicSessions'
import { ApiError } from '../lib/api'
import { env } from '../lib/env'
import { formatPesewas } from '../lib/money'
import { dialablePhone } from '../lib/phone'

/**
 * Specialist profile (M12-T05; enriched + appointment entry M14-T07) at
 * `/specialists/:slug`. Photo, specialty, experience, qualifications,
 * sub-specialties, languages, consult fee, bio, and the partner hospitals they
 * practise at — each with clinic sessions and a "Request an appointment" CTA
 * (M14-T08), Directions, and a Call link when that hospital's number is dialable
 * (M14-T17). A persistent "Call AMTC for help" button covers the case where a
 * hospital doesn't pick up — or has no usable number. Loading, not-found, and
 * error states are explicit.
 */
export function SpecialistProfile(): ReactNode {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: specialistKey(slug),
    queryFn: ({ signal }) => fetchSpecialist(slug, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.code === 'not_found' ? false : count < 2,
  })
  const notFound =
    query.error instanceof ApiError && query.error.code === 'not_found'

  return (
    <PageShell measure={594}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">Specialist</span>
      </header>

      {/* One band around every branch — loading, not-found, error and the
          profile itself all inherit the measure, and none of them moves a
          padding value. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {query.isPending ? (
          <p
            className="px-4 py-12 text-center text-[13px] text-ink-3"
            role="status"
          >
            Loading…
          </p>
        ) : notFound ? (
          <NotFound onBack={() => navigate('/specialists')} />
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-1 flex-col items-center justify-center gap-1 px-5 py-16 text-center"
          >
            <p className="text-[13px] text-ink-3">
              Couldn&apos;t load this specialist.
            </p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="mt-1 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col px-4 py-4">
            <div className="flex items-center gap-3.5">
              <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-bg-sunken text-ink-3">
                <Icon name="user" size={28} />
              </span>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-ink-1">
                  {query.data.full_name}
                </h1>
                <p className="mt-0.5 text-[12.5px] font-medium text-accent-ink">
                  {query.data.specialty}
                </p>
                <p className="mt-0.5 text-[11.5px] text-ink-3">
                  {query.data.languages.join(' · ')}
                  {query.data.consult_fee_pesewas != null && (
                    <>
                      {' · consult '}
                      <span
                        className="tabular-nums"
                        aria-label={`Consultation fee ${formatPesewas(query.data.consult_fee_pesewas)}`}
                      >
                        {formatPesewas(query.data.consult_fee_pesewas)}
                      </span>
                    </>
                  )}
                </p>
                {(query.data.years_experience != null ||
                  (query.data.qualifications &&
                    query.data.qualifications.length > 0)) && (
                  <p className="mt-0.5 text-[11.5px] text-ink-3">
                    {query.data.qualifications?.join(', ')}
                    {query.data.qualifications &&
                      query.data.qualifications.length > 0 &&
                      query.data.years_experience != null &&
                      ' · '}
                    {query.data.years_experience != null &&
                      `${query.data.years_experience} yrs experience`}
                  </p>
                )}
              </div>
            </div>

            {query.data.sub_specialties &&
              query.data.sub_specialties.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {query.data.sub_specialties.map((s) => (
                    <li
                      key={s}
                      className="rounded-full bg-bg-sunken px-2 py-0.5 text-[11px] text-ink-2"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}

            {query.data.bio && (
              <p className="mt-3.5 text-[13px] leading-relaxed text-ink-2">
                {query.data.bio}
              </p>
            )}

            <h2 className="mb-2 mt-4 font-mono text-[10px] uppercase tracking-wide text-ink-3">
              Where &amp; when
            </h2>
            {query.data.hospitals.length === 0 ? (
              <p className="text-[13px] text-ink-3">
                Not currently listed at a partner hospital.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {query.data.hospitals.map((h) => {
                  const hasSessions = (h.sessions?.length ?? 0) > 0
                  // Null whenever the hospital's number is missing or still the
                  // seed placeholder — we then show no Call, and the persistent
                  // "Call AMTC for help" below remains the way through.
                  const hospitalPhone = dialablePhone(h.phone)
                  return (
                    <li
                      key={h.slug}
                      className="rounded-r3 border border-line-1 bg-bg-app p-3"
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex-1 text-[13.5px] font-semibold text-ink-1">
                          {h.name}
                        </span>
                        {h.is_primary && (
                          <span className="rounded-full bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-3">
                        <Icon name="pin" size={10} /> {h.city}
                      </div>

                      <ClinicSessions
                        sessions={h.sessions}
                        availabilityNote={h.availability_note ?? null}
                      />

                      <div className="mt-2.5 flex items-center gap-2">
                        {hasSessions ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/specialists/${query.data.slug}/request/${h.slug}`,
                              )
                            }
                            className="flex-1 rounded-r2 bg-accent px-3 py-2 text-center text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                          >
                            Request an appointment
                          </button>
                        ) : (
                          <span className="flex-1 text-[12px] text-ink-3">
                            Contact the hospital to arrange a visit.
                          </span>
                        )}
                        {hospitalPhone && (
                          <a
                            href={`tel:${hospitalPhone}`}
                            aria-label={`Call ${h.name}`}
                            className="inline-flex items-center gap-1 rounded-r2 border border-line-2 px-3 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                          >
                            <Icon name="phone" size={13} /> Call
                          </a>
                        )}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${h.lat},${h.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-r2 border border-line-2 px-3 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          <Icon name="pin" size={13} /> Directions
                        </a>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}

            {/* Always-available help: hospitals don't always pick up, so a patient
              can reach the AMTC team to finish arranging the appointment. */}
            <a
              href={`tel:${env.amtcPhone}`}
              className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-r3 border border-accent bg-accent-bg px-4 py-2.5 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Icon name="phone" size={14} /> Call AMTC for help
            </a>
          </div>
        )}
      </PageBand>
    </PageShell>
  )
}

function NotFound({ onBack }: { onBack: () => void }): ReactNode {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1 px-5 py-16 text-center">
      <h2 className="text-base font-semibold text-ink-1">
        Specialist not found
      </h2>
      <p className="text-[13px] text-ink-3">
        That specialist isn&apos;t in our list.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="mt-3 rounded-full border border-line-2 bg-bg-app px-4 py-1.5 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Back to specialists
      </button>
    </div>
  )
}
